/**
 * De cijfers die de organisator tijdens het evenement ziet.
 *
 * Het dashboard rekent zelf niets uit: het haalt de bonnen op en geeft ze
 * hieraan door. Zo staat er maar één berekening in het project en klopt het
 * dashboard per definitie met de afrekening achteraf.
 *
 * Statiegeld telt niet mee in de omzet en niet in de commissie (regel 10).
 */

import { berekenCommissie } from './commissie.js';
import { afrondCenten, sommeer, type Basispunten, type Cent } from './geld.js';

/** Eén afgeronde bon, zoals het dashboard hem van de server krijgt. */
export interface BonSamenvatting {
  kraamdeelnameId: string;
  /** Wat de klant betaalde, inclusief het statiegeldsaldo van die bon. */
  totaalCent: Cent;
  /** Het statiegeldsaldo dat in dat bedrag zat. */
  statiegeldCent: Cent;
  afgerondOp: string;
}

export interface KraamInvoer {
  kraamdeelnameId: string;
  kraamnaam: string;
  commissieBasispunten: Basispunten;
}

export interface KraamCijfers extends KraamInvoer {
  aantalBonnen: number;
  /** Alleen producten; statiegeld zit er niet in. */
  omzetCent: Cent;
  statiegeldSaldoCent: Cent;
  commissieCent: Cent;
  gemiddeldeBonCent: Cent;
}

export interface UurCijfers {
  /** Uur in Amsterdamse tijd, 0 t/m 23. */
  uur: number;
  aantalBonnen: number;
  omzetCent: Cent;
}

export interface Evenementcijfers {
  aantalBonnen: number;
  omzetCent: Cent;
  statiegeldSaldoCent: Cent;
  /** Wat de organisator naar verwachting overhoudt aan commissie. */
  verwachteCommissieCent: Cent;
  gemiddeldeBonCent: Cent;
  perKraam: KraamCijfers[];
  perUur: UurCijfers[];
}

function uurVan(tijdstip: string, tijdzone: string): number {
  return Number(
    new Intl.DateTimeFormat('nl-NL', { timeZone: tijdzone, hour: '2-digit', hour12: false }).format(
      new Date(tijdstip),
    ),
  );
}

/**
 * Wat de database per kraam en per uur optelt (functie `evenement_live`).
 * Een evenement met tienduizenden bonnen levert zo een paar honderd rijen op,
 * en de server kapt dan ook niets af.
 */
export interface UurTelling {
  kraamdeelnameId: string;
  /** Uur in Amsterdamse tijd, 0 t/m 23. */
  uur: number;
  aantalBonnen: number;
  /** Wat de klanten betaalden, inclusief het statiegeldsaldo. */
  totaalCent: Cent;
  statiegeldCent: Cent;
}

/** Zet losse bonnen om naar dezelfde telling die de database teruggeeft. */
export function telBonnenPerUur(
  bonnen: readonly BonSamenvatting[],
  tijdzone = 'Europe/Amsterdam',
): UurTelling[] {
  const kaart = new Map<string, UurTelling>();
  for (const bon of bonnen) {
    const uur = uurVan(bon.afgerondOp, tijdzone);
    const sleutel = `${bon.kraamdeelnameId}|${uur}`;
    const bestaand = kaart.get(sleutel);
    if (bestaand === undefined) {
      kaart.set(sleutel, {
        kraamdeelnameId: bon.kraamdeelnameId,
        uur,
        aantalBonnen: 1,
        totaalCent: bon.totaalCent,
        statiegeldCent: bon.statiegeldCent,
      });
    } else {
      bestaand.aantalBonnen += 1;
      bestaand.totaalCent += bon.totaalCent;
      bestaand.statiegeldCent += bon.statiegeldCent;
    }
  }
  return [...kaart.values()];
}

export function maakEvenementcijfers(
  kramen: readonly KraamInvoer[],
  bonnen: readonly BonSamenvatting[],
  tijdzone = 'Europe/Amsterdam',
): Evenementcijfers {
  return maakEvenementcijfersUitTelling(kramen, telBonnenPerUur(bonnen, tijdzone));
}

/**
 * De enige plek waar de cijfers van een evenement worden uitgerekend. Of de
 * telling uit losse bonnen komt of rechtstreeks uit de database maakt niet uit.
 */
export function maakEvenementcijfersUitTelling(
  kramen: readonly KraamInvoer[],
  telling: readonly UurTelling[],
): Evenementcijfers {
  const perKraam: KraamCijfers[] = kramen.map((kraam) => {
    const eigen = telling.filter((rij) => rij.kraamdeelnameId === kraam.kraamdeelnameId);
    const aantalBonnen = eigen.reduce((som, rij) => som + rij.aantalBonnen, 0);
    const statiegeldSaldoCent = sommeer(eigen.map((rij) => rij.statiegeldCent));
    const omzetCent = sommeer(eigen.map((rij) => rij.totaalCent)) - statiegeldSaldoCent;

    return {
      ...kraam,
      aantalBonnen,
      omzetCent,
      statiegeldSaldoCent,
      commissieCent: berekenCommissie({ brutoOmzetCent: omzetCent }, kraam.commissieBasispunten)
        .commissieCent,
      gemiddeldeBonCent: aantalBonnen === 0 ? 0 : afrondCenten(omzetCent / aantalBonnen),
    };
  });

  // Alleen kramen die meedoen tellen mee, net als de kramen hierboven.
  const bekend = new Set(kramen.map((kraam) => kraam.kraamdeelnameId));
  const perUurKaart = new Map<number, UurCijfers>();
  for (const rij of telling) {
    if (!bekend.has(rij.kraamdeelnameId)) continue;
    const omzet = rij.totaalCent - rij.statiegeldCent;
    const bestaand = perUurKaart.get(rij.uur);
    if (bestaand === undefined) {
      perUurKaart.set(rij.uur, { uur: rij.uur, aantalBonnen: rij.aantalBonnen, omzetCent: omzet });
    } else {
      bestaand.aantalBonnen += rij.aantalBonnen;
      bestaand.omzetCent += omzet;
    }
  }

  const aantalBonnen = perKraam.reduce((som, kraam) => som + kraam.aantalBonnen, 0);
  const omzetCent = sommeer(perKraam.map((kraam) => kraam.omzetCent));

  return {
    aantalBonnen,
    omzetCent,
    statiegeldSaldoCent: sommeer(perKraam.map((kraam) => kraam.statiegeldSaldoCent)),
    verwachteCommissieCent: sommeer(perKraam.map((kraam) => kraam.commissieCent)),
    gemiddeldeBonCent: aantalBonnen === 0 ? 0 : afrondCenten(omzetCent / aantalBonnen),
    perKraam: perKraam.sort((a, b) => b.omzetCent - a.omzetCent),
    perUur: [...perUurKaart.values()].sort((a, b) => a.uur - b.uur),
  };
}
